import React, { useState, useEffect } from 'react';
import { PageHeader, MetricCard, SectionCard, LoadingState, ErrorState } from '../../components/common';
import { theme } from '../../design/theme';
import { aiOrchestrationService } from './aiOrchestrationService';

export const AIOrchestrationDashboard: React.FC = () => {
  const [orchestrationData, setOrchestrationData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrchestrationData = async () => {
      try {
        const data = await aiOrchestrationService.getStatus();
        setOrchestrationData(data);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch orchestration metrics');
      } finally {
        setLoading(false);
      }
    };
    fetchOrchestrationData();
  }, []);

  if (loading) return <LoadingState />;
  if (error || !orchestrationData) return <ErrorState message={error || "Orchestration data unavailable"} />;

  return (
    <div>
      <PageHeader 
        title="AI Orchestration & Emergent Intelligence" 
        subtitle="Manage and monitor distributed AI agents and autonomous code execution."
      />
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: theme.spacing.lg,
        marginBottom: theme.spacing.xl
      }}>
        <MetricCard 
          label="ACTIVE AGENTS" 
          value={orchestrationData.activeAgents.toString()} 
          status="healthy" 
        />
        <MetricCard 
          label="SUCCESS RATE %" 
          value={`${orchestrationData.successRate}%`} 
          status={orchestrationData.successRate < 90 ? 'warning' : 'healthy'} 
        />
        <MetricCard 
          label="TOTAL EXECUTIONS" 
          value={orchestrationData.totalExecutions.toString()} 
          status="healthy" 
        />
        <MetricCard 
          label="AVG LATENCY (MS)" 
          value={orchestrationData.avgLatency.toString()} 
          status={orchestrationData.avgLatency > 1000 ? 'warning' : 'healthy'} 
        />
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '2fr 1fr', 
        gap: theme.spacing.lg 
      }}>
        <SectionCard title="Live Execution Log">
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
            {orchestrationData.recentTasks.map((task: any) => (
              <div key={task.id} style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                padding: theme.spacing.sm,
                backgroundColor: theme.colors.backgroundAlt,
                borderRadius: '4px'
              }}>
                <span style={theme.typography.bodyBold}>{task.name}</span>
                <div style={{ display: 'flex', gap: theme.spacing.md }}>
                   <span style={{ 
                     ...theme.typography.caption,
                     color: task.status === 'completed' ? theme.colors.success : theme.colors.warning
                   }}>{task.status.toUpperCase()}</span>
                   <span style={theme.typography.caption}>{task.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
        
        <SectionCard title="Emergent Intelligence Status">
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={theme.typography.body}>Self-Healing Engine</span>
              <span style={{ color: theme.colors.success }}>ONLINE</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={theme.typography.body}>Autonomous Refactoring</span>
              <span style={{ color: theme.colors.success }}>STANDBY</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={theme.typography.body}>Predictive Debugging</span>
              <span style={{ color: theme.colors.warning }}>CALIBRATING</span>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};
